/**
 * Zod schemas for every branded primitive.
 *
 * Convention: one `X_SCHEMA` per brand, plus a runtime constructor
 * `toX(raw): X` that funnels through the schema. This keeps zod as the
 * single truth for validation and puts the brand cast in exactly one place
 * (see `zod-helpers.ts`).
 */

import type { z } from "zod";
import { brandedInt, brandedString } from "./zod-helpers.js";
import {
  ABILITY_ID_PATTERN,
  AURA_ID_PATTERN,
  CHAMPION_ID_PATTERN,
  CONTENT_HASH_PATTERN,
  CONTENT_ID_LEN,
  MAP_ID_PATTERN,
  MAX_ABILITY_SLOT,
  MAX_HP,
  MAX_ID_LEN,
  MAX_SEAT,
  MAX_SEQ,
  MAX_STATE_HASH,
  MAX_TEAM,
  MAX_TICK,
  MAX_UNITS,
  MAX_UNITS_PER_TICK,
  MAX_WEIGHT,
  MIN_HP,
  MIN_UNITS,
  MIN_UNITS_PER_TICK,
  MIN_WEIGHT,
} from "./limits.js";
import { BrandError } from "./errors.js";
import type {
  ModeId,
  ChampionId,
  AuraDefId,
  AbilityId,
  MapId,
  ResourceId,
  PointsId,
  AbilitySlot,
  ContentHash,
  EntityId,
  Hp,
  MatchId,
  SeatIndex,
  Seq,
  StateHash,
  TeamIndex,
  Tick,
  Units,
  UnitsPerTick,
  Weight,
} from "./brands.js";

// ─── Schemas ───────────────────────────────────────────────────────────────

export const TICK_SCHEMA = brandedInt<Tick>("Tick", 0, MAX_TICK);
export const UNITS_SCHEMA = brandedInt<Units>("Units", MIN_UNITS, MAX_UNITS);
export const UNITS_PER_TICK_SCHEMA = brandedInt<UnitsPerTick>(
  "UnitsPerTick",
  MIN_UNITS_PER_TICK,
  MAX_UNITS_PER_TICK,
);
export const WEIGHT_SCHEMA = brandedInt<Weight>("Weight", MIN_WEIGHT, MAX_WEIGHT);
export const HP_SCHEMA = brandedInt<Hp>("Hp", MIN_HP, MAX_HP);
export const SEAT_INDEX_SCHEMA = brandedInt<SeatIndex>("SeatIndex", 0, MAX_SEAT);
export const TEAM_INDEX_SCHEMA = brandedInt<TeamIndex>("TeamIndex", 0, MAX_TEAM);
export const ABILITY_SLOT_SCHEMA = brandedInt<AbilitySlot>(
  "AbilitySlot",
  0,
  MAX_ABILITY_SLOT,
);
export const SEQ_SCHEMA = brandedInt<Seq>("Seq", 0, MAX_SEQ);
export const STATE_HASH_SCHEMA = brandedInt<StateHash>(
  "StateHash",
  0,
  MAX_STATE_HASH,
);

export const ENTITY_ID_SCHEMA = brandedString<EntityId>("EntityId", 1, MAX_ID_LEN);
export const MATCH_ID_SCHEMA = brandedString<MatchId>("MatchId", 1, MAX_ID_LEN);
export const MODE_ID_SCHEMA = brandedString<ModeId>("ModeId", 1, MAX_ID_LEN);
// #163: the three MINTED content-id schemas. Fixed length 14 (`<prefix>_` + a
// 10-char base62 nanoid) + a canonical prefix pattern, mirroring
// `CONTENT_HASH_SCHEMA`. Enforced at the loader/wire boundary (fail loud on a
// malformed id); the compile-time brand delivers cross-type safety everywhere.
export const AURA_DEF_ID_SCHEMA = brandedString<AuraDefId>(
  "AuraDefId",
  CONTENT_ID_LEN,
  CONTENT_ID_LEN,
  AURA_ID_PATTERN,
);
export const CHAMPION_ID_SCHEMA = brandedString<ChampionId>(
  "ChampionId",
  CONTENT_ID_LEN,
  CONTENT_ID_LEN,
  CHAMPION_ID_PATTERN,
);
export const MAP_ID_SCHEMA = brandedString<MapId>(
  "MapId",
  CONTENT_ID_LEN,
  CONTENT_ID_LEN,
  MAP_ID_PATTERN,
);
// #190: ability def id — same fixed-length `<prefix>_<10-char base62>` shape as
// the other minted content ids, enforced at the loader boundary.
export const ABILITY_ID_SCHEMA = brandedString<AbilityId>(
  "AbilityId",
  CONTENT_ID_LEN,
  CONTENT_ID_LEN,
  ABILITY_ID_PATTERN,
);
export const RESOURCE_ID_SCHEMA = brandedString<ResourceId>(
  "ResourceId",
  1,
  MAX_ID_LEN,
);
export const POINTS_ID_SCHEMA = brandedString<PointsId>(
  "PointsId",
  1,
  MAX_ID_LEN,
);
export const CONTENT_HASH_SCHEMA = brandedString<ContentHash>(
  "ContentHash",
  9, // "sha256:" + at least one hex byte would be 8; require full 71.
  71,
  CONTENT_HASH_PATTERN,
);

// ─── Constructors ──────────────────────────────────────────────────────────

/**
 * Build a branded value from a raw number/string. Throws BrandError on any
 * validation failure. Use ONLY when the value is internally constructed
 * (not from the wire) — wire values go through `parseX` → Result.
 */
function make<B, In>(brand: string, schema: z.ZodType<B, In>, raw: In): B {
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    const detail = parsed.error.issues[0]?.message ?? "invalid value";
    throw new BrandError(brand, raw, detail);
  }
  return parsed.data;
}

export const toTick = (n: number): Tick => make("Tick", TICK_SCHEMA, n);
export const toUnits = (n: number): Units => make("Units", UNITS_SCHEMA, n);
export const toUnitsPerTick = (n: number): UnitsPerTick =>
  make("UnitsPerTick", UNITS_PER_TICK_SCHEMA, n);
export const toWeight = (n: number): Weight => make("Weight", WEIGHT_SCHEMA, n);
export const toHp = (n: number): Hp => make("Hp", HP_SCHEMA, n);
export const toSeatIndex = (n: number): SeatIndex =>
  make("SeatIndex", SEAT_INDEX_SCHEMA, n);
export const toTeamIndex = (n: number): TeamIndex =>
  make("TeamIndex", TEAM_INDEX_SCHEMA, n);
export const toAbilitySlot = (n: number): AbilitySlot =>
  make("AbilitySlot", ABILITY_SLOT_SCHEMA, n);
export const toSeq = (n: number): Seq => make("Seq", SEQ_SCHEMA, n);
export const toStateHash = (n: number): StateHash =>
  make("StateHash", STATE_HASH_SCHEMA, n);

export const toEntityId = (s: string): EntityId =>
  make("EntityId", ENTITY_ID_SCHEMA, s);
export const toMatchId = (s: string): MatchId =>
  make("MatchId", MATCH_ID_SCHEMA, s);
export const toModeId = (s: string): ModeId =>
  make("ModeId", MODE_ID_SCHEMA, s);
export const toChampionId = (s: string): ChampionId =>
  make("ChampionId", CHAMPION_ID_SCHEMA, s);
export const toMapId = (s: string): MapId => make("MapId", MAP_ID_SCHEMA, s);
export const toAbilityId = (s: string): AbilityId =>
  make("AbilityId", ABILITY_ID_SCHEMA, s);
export const toAuraDefId = (s: string): AuraDefId =>
  make("AuraDefId", AURA_DEF_ID_SCHEMA, s);
export const toResourceId = (s: string): ResourceId =>
  make("ResourceId", RESOURCE_ID_SCHEMA, s);
export const toPointsId = (s: string): PointsId =>
  make("PointsId", POINTS_ID_SCHEMA, s);
export const toContentHash = (s: string): ContentHash =>
  make("ContentHash", CONTENT_HASH_SCHEMA, s);

// ─── Content-id prefix registry + typeOf guard (#163) ────────────────────────

/**
 * The content-id prefix registry (#163). Maps each minted content CLASS to its
 * 3-lowercase-letter prefix. `aur`/`chm`/`map`/`abl` are LIVE (a real reference
 * target with an id field); the rest are RESERVED — the prefix is booked so the
 * scheme can add an id field later without a reshape, but NO class has an id
 * field yet (`abl` graduated to live in #190 — a champion references abilities by
 * id; effects are addressed by array position, tags are free-form selectors —
 * neither is a stable reference target; items/modes are the parked platform/game
 * split). A prefix is PERMANENTLY exactly 3 lowercase ASCII letters, so `typeOf`
 * can split on the first `_` with zero ambiguity and a human can eyeball the type.
 */
export const ID_PREFIX = {
  // ── minted (a real reference target, has an `id` field) ──
  aura: "aur",
  champion: "chm",
  map: "map",
  // #190: abilities are now a first-class reference target (a champion's
  // `abilities` array is a list of `abl_` ids), so `abl_` graduates from
  // reserved to minted alongside aura/champion/map.
  ability: "abl",
  // ── reserved (prefix booked, NO id field yet — see #163 §7.4) ──
  effect: "eff",
  tag: "tag",
  item: "itm",
  mode: "mod",
} as const;

/** A content class name known to the prefix registry. */
export type ContentIdClass = keyof typeof ID_PREFIX;

/**
 * Table-driven runtime guard (#163): read an id's CLASS from its prefix, or
 * `null` if the id is malformed / carries an unknown prefix. The prefix is the
 * 3 chars before the FIRST `_` (prefixes are `[a-z]{3}` and the base62 random
 * part excludes `_`, so the first `_` is the unambiguous split). Pure; the SAME
 * table backs every branded id schema's prefix, so the guard and the schemas can
 * never disagree about which prefix means which class.
 */
export function typeOf(id: string): ContentIdClass | null {
  const i = id.indexOf("_");
  const p = i === 3 ? id.slice(0, 3) : "";
  if (p === "") return null;
  for (const [cls, prefix] of Object.entries(ID_PREFIX)) {
    if (prefix === p) return cls as ContentIdClass;
  }
  return null;
}
